import i18n from '@/dependencies/i18n';
import ColorHash from 'color-hash';
import { TrackingListTypes } from '@/types/utility.types';
import { TranslatedMessage } from '@/types/common.types';
import { isNull, isUndefined } from 'lodash';

const { locale } = i18n.global;

class ColorGenerator {
    _colorHelper: any;

    constructor() {
        this._colorHelper = new ColorHash({
            saturation: [0.3, 0.5],
            lightness: [0.4, 0.6],
        });
    }

    getHex(value: string) {
        if (!value) return;
        return this._colorHelper.hex(value.split('').reverse().join(''));
    }
}

export const ColorHelper = new ColorGenerator();

export function convertToLocaleString(amount: number | string, options?: Intl.NumberFormatOptions & { sign?: boolean }) {
    if (typeof amount !== 'number') {
        return amount;
    }

    options = {
        ...{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            sign: true,
            trailingZeroDisplay: 'stripIfInteger',
        },
        ...options,
    };

    amount = amount.toLocaleString(locale.value, options);

    return options.sign ? `€${amount}` : amount;
}

export function isFalseAndNotUndefined(value) {
    return value === false && value !== undefined;
}

export function formatToUnderscoreUpperCase(value: string) {
    if (!value || typeof value !== 'string') {
        return '';
    }

    return value.split('-').join('_').toUpperCase();
}

export function getParameterByName(name) {
    name = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(window.location.href);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function snakeCase(n: string) {
    if (!n) return;
    return n
        .replace(/\W+/g, ' ')
        .split(/ |\B(?=[A-Z])/)
        .map((word) => word.toLowerCase())
        .join('_');
}

export function capitalizeFirstLetter(string: string) {
    if (!string) return;
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function isUndefinedOrNull(value: any) {
    return isUndefined(value) || isNull(value);
}

export function newTrackingList(): TrackingListTypes {
    const list: TrackingListTypes = [] as unknown as TrackingListTypes;

    list.add = (id: string) => {
        if (list.includes(id)) {
            return;
        }

        list.push(id);
    };

    list.remove = (id: string) => {
        list.splice(
            list.findIndex((currId) => currId === id),
            1,
        );
    };

    return list;
}

export function getTranslatedName(data: any) {
    return data?.find((option) => option.locale === locale.value)?.value ?? 'Missing translation';
}

export function getTranslatedValue(data: TranslatedMessage[], locale: string, fallback = '') {
    return data?.find((option) => option.locale === locale)?.value ?? fallback;
}

export function isNonEmptyArray<T>(value: T[]): value is [T, ...T[]] {
    return value.length > 0;
}

export function removeEmpty<R, T extends { [s: string]: R } | ArrayLike<R>>(obj: T) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v)) as T;
}

export function removeEmptyObjects<T extends object>(obj: T): Partial<T> {
    return Object.fromEntries(
        Object.entries(obj)
            .map(([key, value]) => [
                key,
                value && typeof value === 'object' && !Array.isArray(value)
                    ? removeEmptyObjects(value)
                    : value,
            ])
            .filter(([, value]) => {
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    return Object.keys(value).length > 0;
                }
                return true;
            })
    ) as Partial<T>;
}

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
}

export function ellipsis(value, numCharacters = 6) {
    if (!value) {
        return '';
    }
    if (value?.length > numCharacters) {
        return value.substring(0, numCharacters) + '...';
    }
    return value;
}

export function isTranslationKey(value: string) {
    return /[A-Z0-9]+(\.[A-Z0-9_]+)*\.[A-Z0-9_]+$/.test(value);
}

function mod97(value: string) {
    let checksum = parseInt(value.slice(0, 2));
    let fragment;

    for (let offset = 2; offset < value.length; offset += 7) {
        fragment = String(checksum) + value.substring(offset, offset + 7);
        checksum = parseInt(fragment, 10) % 97;
    }

    return checksum;
}

export function isValidIBANNumber(input: string) {
    const CODE_LENGTHS = {
        AD: 24,
        AE: 23,
        AT: 20,
        AZ: 28,
        BA: 20,
        BE: 16,
        BG: 22,
        BH: 22,
        BR: 29,
        CH: 21,
        CY: 28,
        CZ: 24,
        DE: 22,
        DK: 18,
        DO: 28,
        EE: 20,
        ES: 24,
        FI: 18,
        FO: 18,
        FR: 27,
        GB: 22,
        GI: 23,
        GL: 18,
        GR: 27,
        GT: 28,
        HR: 21,
        HU: 28,
        IE: 22,
        IL: 23,
        IS: 26,
        IT: 27,
        JO: 30,
        KW: 30,
        KZ: 20,
        LB: 28,
        LI: 21,
        LT: 20,
        LU: 20,
        LV: 21,
        MC: 27,
        MD: 24,
        ME: 22,
        MK: 19,
        MR: 27,
        MT: 31,
        MU: 30,
        NL: 18,
        NO: 15,
        PK: 24,
        PL: 28,
        PS: 29,
        PT: 25,
        QA: 29,
        RO: 24,
        RS: 22,
        SA: 24,
        SE: 24,
        SI: 19,
        SK: 24,
        SM: 27,
        TN: 24,
        TR: 26,
        AL: 28,
        BY: 28,
        CR: 22,
        EG: 29,
        GE: 22,
        IQ: 23,
        LC: 32,
        SC: 31,
        ST: 25,
        SV: 28,
        TL: 23,
        UA: 29,
        VA: 22,
        VG: 24,
        XK: 20,
    };

    const iban = String(input)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, ''); // keep only alphanumeric characters
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/); // match and capture (1) the country code, (2) the check digits, and (3) the rest

    // check syntax and length
    if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
        return false;
    }

    // rearrange country code and check digits, and convert chars to ints
    const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter) => {
        return (letter.charCodeAt(0) - 55).toString();
    });

    // final check
    return mod97(digits) === 1;
}

export function getShadedColor(hex: string, adjustment = 200): string {
    // 1. Sanitize and prepare the hex string
    let color = hex.startsWith('#') ? hex.substring(1) : hex;

    // Handle shorthand hex codes (e.g., 'F00' -> 'FF0000')
    if (color.length === 3) {
        color = color
            .split('')
            .map((c) => c + c)
            .join('');
    }

    if (color.length !== 6) {
        return '';
    }

    // Convert hex to RGB components
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    // 2. Determine if the color is light or dark
    // Use the perceived brightness formula (Luma) to decide
    // 0.299*R + 0.587*G + 0.114*B (Standard NTSC/Rec. 709 luminance)
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    const isLight = luma > 128; // 128 is the midpoint for 0-255

    // 3. Adjust the RGB values
    // If the color is light, we want to *darken* the shade, so use a negative adjustment.
    // If the color is dark, we want to *lighten* the shade, so use a positive adjustment.
    const finalAdjustment = isLight ? -Math.abs(adjustment) : Math.abs(adjustment);

    const newR = Math.max(0, Math.min(255, r + finalAdjustment));
    const newG = Math.max(0, Math.min(255, g + finalAdjustment));
    const newB = Math.max(0, Math.min(255, b + finalAdjustment));

    // Helper function to convert a single color component back to a 2-digit hex string
    const toHex = (c) => {
        const hex = Math.round(c).toString(16);

        return hex.length === 1 ? '0' + hex : hex;
    };

    // 4. Convert the new RGB values back into a hex string
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}
